import { useState, useMemo, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Search, Settings, Clock, CheckCircle2, X, Gauge, Plus, Pencil, Trash2, DollarSign, Loader2 } from 'lucide-react';
import { maintenanceServicesData as fallbackData } from '../data/maintenanceServicesData';
import ExcelUpload from '../components/ExcelUpload';
import apiService from '../services/api';

const MaintenanceServicesList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    serviceName: '',
    mileage: '',
    interval: '',
    serviceType: 'Basic Service',
    durationMin: '',
    durationMax: '',
    priceMin: '',
    priceMax: '',
    description: '',
    includes: ''
  });

  const serviceTypeOptions = [
    'Basic Service',
    'Minor Service',
    'Intermediate Service',
    'Major Service',
    'Major/Extended Service',
    'Extended Life Service'
  ];

  // Excel upload configuration
  const excelExpectedColumns = ['Service Name', 'Mileage', 'Service Type', 'Duration Min', 'Duration Max', 'Price Min', 'Price Max'];
  const excelTemplateData = [
    { 
      'Service Name': '30,000 Miles Maintenance Service', 
      'Mileage': 30000,
      'Interval': '36 months',
      'Service Type': 'Intermediate Service',
      'Duration Min': 90, 
      'Duration Max': 150, 
      'Price Min': 300, 
      'Price Max': 500,
      'Description': 'Comprehensive maintenance with fluid inspections and replacements',
      'What Is Included': 'Oil & filter change; Tire rotation; Check brakes; Inspect belts & hoses'
    }
  ];

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new Master Maintenance Service API
      const response = await apiService.getMasterMaintenanceServices();
      if (response.success && response.data?.services) {
        // Transform API data to our format
        // API fields: title, intervalMiles, level, intervalTime,
        // durationRangeMinutes: { minMinutes, maxMinutes },
        // constRangeMinutes: { minPrice, maxPrice } (note: "const" is intentional legacy typo)
        const transformedData = response.data.services.map(service => ({
          id: service._id,
          serviceName: service.title || 'Unknown',
          mileage: service.intervalMiles || 0,
          interval: service.intervalTime || 'As needed',
          serviceType: service.level || 'Basic Service',
          durationMin: service.durationRangeMinutes?.minMinutes || 0,
          durationMax: service.durationRangeMinutes?.maxMinutes || 0,
          priceMin: service.constRangeMinutes?.minPrice || 0,
          priceMax: service.constRangeMinutes?.maxPrice || 0,
          description: service.description || '',
          includes: [],
          isActive: service.isActive
        }));
        setData(transformedData);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching maintenance services:', err);
      setError('Failed to load data from server. Showing offline data.');
      // Use fallback data
      setData(fallbackData.map(item => ({ ...item })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Excel data import
  const handleExcelImport = (excelData) => {
    const maxId = Math.max(...data.map(s => typeof s.id === 'number' ? s.id : 0), 0);
    
    const newServices = excelData.map((row, index) => ({
      id: `new-${maxId + index + 1}`,
      serviceName: row['Service Name'] || row.serviceName || '',
      mileage: parseInt(row['Mileage'] || row.mileage) || 0,
      interval: row['Interval'] || row.interval || 'As needed',
      serviceType: row['Service Type'] || row.serviceType || 'Basic Service',
      durationMin: parseInt(row['Duration Min'] || row.durationMin) || 0,
      durationMax: parseInt(row['Duration Max'] || row.durationMax) || 0,
      priceMin: parseFloat(row['Price Min'] || row.priceMin) || 0,
      priceMax: parseFloat(row['Price Max'] || row.priceMax) || 0,
      description: row['Description'] || row.description || '',
      includes: (row['What Is Included'] || row.includes || '')
        .split(';')
        .map(s => s.trim())
        .filter(s => s.length > 0)
    })).filter(s => s.serviceName);

    setData(prev => [...prev, ...newServices].sort((a, b) => a.mileage - b.mileage));
  };

  // Get unique service types
  const serviceTypes = useMemo(() => {
    const types = [...new Set(data.map(s => s.serviceType))];
    return types.sort();
  }, [data]);

  // Filter data based on search and type filter
  const filteredData = useMemo(() => {
    return data.filter(service => {
      // Filter by service type
      if (selectedType !== 'all' && service.serviceType !== selectedType) {
        return false;
      }
      
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          service.serviceName.toLowerCase().includes(query) ||
          service.description.toLowerCase().includes(query) ||
          service.serviceType.toLowerCase().includes(query) ||
          service.includes.some(item => item.toLowerCase().includes(query)) ||
          service.mileage.toString().includes(query)
        );
      }
      
      return true;
    });
  }, [searchQuery, selectedType, data]);

  const openViewDetail = (service) => {
    setSelectedService(service);
    setIsViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedService(null);
  };

  // Open add dialog
  const openAddDialog = () => {
    setFormData({
      serviceName: '',
      mileage: '',
      interval: '',
      serviceType: 'Basic Service',
      durationMin: '',
      durationMax: '',
      priceMin: '',
      priceMax: '',
      description: '',
      includes: ''
    });
    setIsAddDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (e, service) => {
    e.stopPropagation();
    setSelectedService(service);
    setFormData({
      serviceName: service.serviceName,
      mileage: service.mileage.toString(),
      interval: service.interval,
      serviceType: service.serviceType,
      durationMin: service.durationMin?.toString() || '',
      durationMax: service.durationMax?.toString() || '',
      priceMin: service.priceMin?.toString() || '',
      priceMax: service.priceMax?.toString() || '',
      description: service.description,
      includes: service.includes.join('\n')
    });
    setIsEditDialogOpen(true);
  };

  // Handle add service
  const handleAddService = async () => {
    if (!formData.serviceName.trim() || !formData.mileage || !formData.description.trim()) return;
    
    setSaving(true);
    try {
      const serviceData = {
        serviceName: formData.serviceName.trim(),
        mileage: parseInt(formData.mileage) || 0,
        interval: formData.interval.trim() || 'As needed',
        serviceType: formData.serviceType,
        description: formData.description.trim(),
        durationObj: {
          min: parseInt(formData.durationMin) || null,
          max: parseInt(formData.durationMax) || null
        },
        costRangeObj: {
          min: parseFloat(formData.priceMin) || null,
          max: parseFloat(formData.priceMax) || null
        },
        whatIsIncluded: formData.includes
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .map(title => ({ title }))
      };

      // Note: API requires shopMongoId - for now we'll add locally
      const newService = {
        id: `new-${Date.now()}`,
        serviceName: formData.serviceName.trim(),
        mileage: parseInt(formData.mileage) || 0,
        interval: formData.interval.trim() || 'As needed',
        serviceType: formData.serviceType,
        durationMin: parseInt(formData.durationMin) || 0,
        durationMax: parseInt(formData.durationMax) || 0,
        priceMin: parseFloat(formData.priceMin) || 0,
        priceMax: parseFloat(formData.priceMax) || 0,
        description: formData.description.trim(),
        includes: formData.includes
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      };
      
      setData(prev => [...prev, newService].sort((a, b) => a.mileage - b.mileage));
      setIsAddDialogOpen(false);
    } catch (err) {
      console.error('Error adding service:', err);
      alert('Failed to add service. ' + (err.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Handle edit service
  const handleEditService = async () => {
    if (!formData.serviceName.trim() || !formData.mileage || !formData.description.trim() || !selectedService) return;
    
    setSaving(true);
    try {
      // Build update data matching the Master Maintenance Service API format
      // Fields: title, intervalMiles, level, intervalTime,
      // durationRangeMinutes: { minMinutes, maxMinutes },
      // constRangeMinutes: { minPrice, maxPrice } (note: "const" is intentional legacy typo)
      const updateData = {
        title: formData.serviceName.trim(),
        intervalMiles: parseInt(formData.mileage) || null,
        level: formData.serviceType,
        intervalTime: formData.interval.trim() || null,
        durationRangeMinutes: {
          minMinutes: parseInt(formData.durationMin) || null,
          maxMinutes: parseInt(formData.durationMax) || null
        },
        constRangeMinutes: {
          minPrice: parseFloat(formData.priceMin) || null,
          maxPrice: parseFloat(formData.priceMax) || null
        }
      };

      // Only call API if we have a real service ID (24-char hex ObjectId)
      if (selectedService.id && !String(selectedService.id).startsWith('new-') && selectedService.id.length === 24) {
        await apiService.updateMasterMaintenanceService(selectedService.id, updateData);
      }
      
      // Update local state
      setData(prev => prev.map(service => 
        service.id === selectedService.id 
          ? {
              ...service,
              serviceName: formData.serviceName.trim(),
              mileage: parseInt(formData.mileage) || 0,
              interval: formData.interval.trim() || 'As needed',
              serviceType: formData.serviceType,
              durationMin: parseInt(formData.durationMin) || 0,
              durationMax: parseInt(formData.durationMax) || 0,
              priceMin: parseFloat(formData.priceMin) || 0,
              priceMax: parseFloat(formData.priceMax) || 0,
              description: formData.description.trim(),
              includes: formData.includes
                .split('\n')
                .map(s => s.trim())
                .filter(s => s.length > 0)
            }
          : service
      ).sort((a, b) => a.mileage - b.mileage));
      
      setIsEditDialogOpen(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error updating service:', err);
      alert('Failed to save changes. ' + (err.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Handle delete service
  const handleDeleteService = (e, serviceId) => {
    e.stopPropagation();
    const service = data.find(s => s.id === serviceId);
    if (window.confirm(`Are you sure you want to delete "${service?.serviceName}"?`)) {
      setData(prev => prev.filter(s => s.id !== serviceId));
    }
  };

  const getServiceTypeColor = (type) => {
    switch (type) {
      case 'Basic Service':
        return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
      case 'Minor Service':
        return 'bg-[#0EA5E9]/20 text-[#38BDF8] border-[#0EA5E9]/30';
      case 'Intermediate Service':
        return 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30';
      case 'Major Service':
        return 'bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/30';
      case 'Major/Extended Service':
        return 'bg-[#A855F7]/20 text-[#C084FC] border-[#A855F7]/30';
      case 'Extended Life Service':
        return 'bg-[#EC4899]/20 text-[#F472B6] border-[#EC4899]/30';
      default:
        return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    }
  };

  const formatMileage = (mileage) => {
    if (mileage >= 1000) {
      return `${(mileage / 1000).toFixed(0)}K`;
    }
    return mileage.toString();
  };

  // Format duration display
  const formatDuration = (min, max) => {
    if (min && max && min !== max) {
      return `${min}-${max} min`;
    } else if (min || max) {
      return `${min || max} min`;
    }
    return 'Varies';
  };

  // Format price display
  const formatPrice = (min, max) => {
    if (min && max && min !== max) {
      return `$${min}-$${max}`;
    } else if (min || max) {
      return `$${min || max}`;
    }
    return 'Quote';
  };

  // Form dialog content
  const ServiceFormContent = () => (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Service Name *</Label>
          <Input
            value={formData.serviceName}
            onChange={(e) => setFormData(prev => ({ ...prev, serviceName: e.target.value }))}
            placeholder="e.g., 30,000 Miles Maintenance Service"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="service-name-input"
          />
        </div>
        <div>
          <Label className="text-[#94A3B8]">Mileage *</Label>
          <Input
            type="number"
            value={formData.mileage}
            onChange={(e) => setFormData(prev => ({ ...prev, mileage: e.target.value }))}
            placeholder="e.g., 30000"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="mileage-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Service Type *</Label>
          <Select 
            value={formData.serviceType} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
          >
            <SelectTrigger className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0]" data-testid="service-type-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-[#334155]">
              {serviceTypeOptions.map(type => (
                <SelectItem key={type} value={type} className="text-[#E2E8F0] focus:bg-[#334155]">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#94A3B8]">Interval</Label>
          <Input
            value={formData.interval}
            onChange={(e) => setFormData(prev => ({ ...prev, interval: e.target.value }))}
            placeholder="e.g., 36 months"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="interval-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Duration Min (minutes)</Label>
          <Input
            type="number"
            value={formData.durationMin}
            onChange={(e) => setFormData(prev => ({ ...prev, durationMin: e.target.value }))}
            placeholder="e.g., 60"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="duration-min-input"
          />
        </div>
        <div>
          <Label className="text-[#94A3B8]">Duration Max (minutes)</Label>
          <Input
            type="number"
            value={formData.durationMax}
            onChange={(e) => setFormData(prev => ({ ...prev, durationMax: e.target.value }))}
            placeholder="e.g., 120"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="duration-max-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#94A3B8]">Price Min ($)</Label>
          <Input
            type="number"
            value={formData.priceMin}
            onChange={(e) => setFormData(prev => ({ ...prev, priceMin: e.target.value }))}
            placeholder="e.g., 150"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="price-min-input"
          />
        </div>
        <div>
          <Label className="text-[#94A3B8]">Price Max ($)</Label>
          <Input
            type="number"
            value={formData.priceMax}
            onChange={(e) => setFormData(prev => ({ ...prev, priceMax: e.target.value }))}
            placeholder="e.g., 300"
            className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
            data-testid="price-max-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-[#94A3B8]">Description *</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe the maintenance package..."
          rows={2}
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] resize-none"
          data-testid="description-input"
        />
      </div>
      <div>
        <Label className="text-[#94A3B8]">What Is Included</Label>
        <Textarea
          value={formData.includes}
          onChange={(e) => setFormData(prev => ({ ...prev, includes: e.target.value }))}
          placeholder="Enter each item on a new line..."
          rows={6}
          className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] resize-none"
          data-testid="includes-input"
        />
        <p className="text-xs text-[#64748B] mt-1">Enter each included item on a new line</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          <p className="text-[#94A3B8]">Loading maintenance services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="maintenance-services-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Maintenance Services Master List
          </h1>
          <p className="text-[#94A3B8]">
            Mileage-based maintenance schedules and service packages
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExcelUpload
            onDataParsed={handleExcelImport}
            expectedColumns={excelExpectedColumns}
            templateData={excelTemplateData}
            templateFileName="maintenance_services_template.xlsx"
            title="Upload Maintenance Services Data"
          />
          <Button 
            onClick={openAddDialog}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="add-maintenance-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Package
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg text-[#F59E0B]">
          {error}
        </div>
      )}

      {/* Stats Card */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4 mb-6 inline-block">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
            <Settings className="w-5 h-5 text-[#2563EB]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#E2E8F0]">{filteredData.length}</p>
            <p className="text-sm text-[#64748B]">Maintenance Packages</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            type="text"
            placeholder="Search by mileage, service type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1E293B] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11"
            data-testid="search-input"
          />
        </div>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-full sm:w-[220px] bg-[#1E293B] border-[#334155] text-[#E2E8F0]" data-testid="type-filter">
            <SelectValue placeholder="Filter by Service Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-[#334155]">
            <SelectItem value="all" className="text-[#E2E8F0] focus:bg-[#334155]">All Service Types</SelectItem>
            {serviceTypes.map(type => (
              <SelectItem key={type} value={type} className="text-[#E2E8F0] focus:bg-[#334155]">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredData.length === 0 ? (
          <div className="col-span-full text-center py-8 text-[#64748B]">
            No maintenance services found matching your search.
          </div>
        ) : (
          filteredData.map((service) => (
            <div
              key={service.id}
              className="bg-[#1E293B] border border-[#334155] rounded-lg p-5 hover:border-[#2563EB]/50 transition-colors cursor-pointer group"
              onClick={() => openViewDetail(service)}
              data-testid={`maintenance-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-[#EF4444] flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-white">{formatMileage(service.mileage)}</span>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Mile Service</p>
                    <Badge variant="outline" className={`mt-1 ${getServiceTypeColor(service.serviceType)}`}>
                      {service.serviceType}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openEditDialog(e, service)}
                      className="h-7 w-7 text-[#64748B] hover:text-[#E2E8F0] hover:bg-[#334155]"
                      data-testid={`edit-maintenance-${service.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDeleteService(e, service.id)}
                      className="h-7 w-7 text-[#64748B] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                      data-testid={`delete-maintenance-${service.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-[#94A3B8] line-clamp-2 mb-3">
                {service.description}
              </p>

              {/* Duration, Price, and Interval Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="bg-[#334155] text-[#94A3B8] border-[#475569]">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDuration(service.durationMin, service.durationMax)}
                </Badge>
                <Badge variant="outline" className="bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30">
                  <DollarSign className="w-3 h-3 mr-0.5" />
                  {formatPrice(service.priceMin, service.priceMax)}
                </Badge>
                <Badge variant="outline" className="bg-[#334155] text-[#94A3B8] border-[#475569]">
                  {service.interval}
                </Badge>
              </div>

              {/* Preview of includes */}
              <div className="space-y-1 mb-4">
                {service.includes.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-3 h-3 text-[#22C55E] flex-shrink-0" />
                    <span className="text-[#E2E8F0] truncate">{item}</span>
                  </div>
                ))}
                {service.includes.length > 3 && (
                  <p className="text-xs text-[#64748B] pl-5">
                    +{service.includes.length - 3} more items
                  </p>
                )}
              </div>

              <Button 
                variant="ghost" 
                size="sm" 
                className="text-[#2563EB] hover:text-[#60A5FA] hover:bg-[#2563EB]/10 p-0 h-auto"
              >
                View Full Details
              </Button>
            </div>
          ))
        )}
      </div>

      {/* View Service Detail Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-[#EF4444] flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">
                    {selectedService && formatMileage(selectedService.mileage)}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Mile Service</p>
                  <DialogTitle className="text-xl font-bold text-[#E2E8F0]">
                    {selectedService?.serviceType}
                  </DialogTitle>
                  <Badge variant="outline" className="mt-1 bg-[#334155] text-[#94A3B8] border-[#475569]">
                    {selectedService?.interval}
                  </Badge>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeViewDialog}
                className="text-[#64748B] hover:text-[#E2E8F0] hover:bg-[#334155]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </DialogHeader>

          {selectedService && (
            <div className="space-y-6 mt-4">
              {/* Description */}
              <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                <p className="text-[#94A3B8] leading-relaxed">
                  {selectedService.description}
                </p>
              </div>

              {/* Service Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm font-semibold text-[#E2E8F0]">Duration</span>
                  </div>
                  <p className="text-[#94A3B8]">
                    {selectedService.durationMin} - {selectedService.durationMax} minutes
                  </p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-sm font-semibold text-[#E2E8F0]">Price Range</span>
                  </div>
                  <p className="text-[#94A3B8]">
                    ${selectedService.priceMin} - ${selectedService.priceMax}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
                    <Gauge className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Mileage</p>
                    <p className="text-[#E2E8F0] font-semibold">{selectedService.mileage.toLocaleString()} miles</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Interval</p>
                    <p className="text-[#E2E8F0] font-semibold">{selectedService.interval}</p>
                  </div>
                </div>
              </div>

              {/* What Is Included */}
              <div>
                <h4 className="text-sm font-semibold text-[#22C55E] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  What Is Included
                </h4>
                <ul className="space-y-2">
                  {selectedService.includes.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" />
                      <span className="text-[#E2E8F0]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#2563EB]" />
              Add New Maintenance Package
            </DialogTitle>
          </DialogHeader>
          <ServiceFormContent />
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddService}
              disabled={!formData.serviceName.trim() || !formData.mileage || !formData.description.trim() || saving}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="save-maintenance-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Package'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#F59E0B]" />
              Edit Maintenance Package
            </DialogTitle>
          </DialogHeader>
          <ServiceFormContent />
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditService}
              disabled={!formData.serviceName.trim() || !formData.mileage || !formData.description.trim() || saving}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
              data-testid="update-maintenance-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Update Package'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceServicesList;
